// ============================================================================
// MarketWizardSuccess — экран «Готово» после создания заявки.
// Показывает результат, предупреждения о фото и переходы. Никаких окон
// платформы: обычный экран мобильного приложения.
// ============================================================================

import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MarketPhoneState, PhotoUploadResult } from '@/lib/market';
import { MarketPhoneStatus } from './MarketPhoneStatus';

export interface WizardSuccessState {
  requestId: string;
  queued: boolean;
  photoResult: PhotoUploadResult;
  skippedPhotos: number;
  /** Состояние телефонного гейта созданной заявки (undefined — не проверено). */
  phoneState?: MarketPhoneState;
  phoneHint?: string;
}

export function MarketWizardSuccess({
  success,
  onOpenRequest,
  onOpenRequests,
  onHome,
}: {
  success: WizardSuccessState;
  onOpenRequest: (requestId: string) => void;
  onOpenRequests: () => void;
  onHome: () => void;
}) {
  const photoFailures = success.photoResult.failures;
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-8 sm:p-6">
      <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2 className="mt-4 text-lg font-semibold">
          {success.queued ? 'Заявка сохранена' : 'Заявка создана'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {success.queued
            ? 'Нет связи с сервером: заявка сохранена на устройстве и будет отправлена автоматически, когда связь появится.'
            : 'Мы передали заявку мастерам. Следите за статусом в разделе «Мои заявки».'}
        </p>
      </div>

      {success.phoneState === 'unverified' && (
        <MarketPhoneStatus
          state="unverified"
          audience="client"
          hint={success.phoneHint || undefined}
        />
      )}

      {success.skippedPhotos > 0 && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs leading-5 text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Фото ({success.skippedPhotos}) не прикреплены: заявка ещё не создана на сервере.
            Добавьте фотографии в карточке заявки после синхронизации.
          </span>
        </div>
      )}
      {success.photoResult.queued > 0 && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs leading-5 text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {success.photoResult.queued} фото ожидают отправки и появятся в заявке при
            восстановлении связи.
          </span>
        </div>
      )}
      {photoFailures.length > 0 && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3 text-xs leading-5 text-destructive">
          <div className="font-medium">Не удалось прикрепить фото: {photoFailures.length}</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {photoFailures.map((f) => (
              <li key={f.fileName}>{f.fileName}: {f.error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        {!success.queued && (
          <Button className="h-11 w-full rounded-xl" onClick={() => onOpenRequest(success.requestId)}>
            Открыть заявку
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        <Button variant="outline" className="h-11 w-full rounded-xl" onClick={onOpenRequests}>
          Мои заявки
        </Button>
        <Button variant="ghost" className="h-10 w-full rounded-xl text-muted-foreground" onClick={onHome}>
          На главную
        </Button>
      </div>
    </div>
  );
}
